import React, { useState } from "react";
import axios from "axios";
import { Calendar, Clock, Globe, Mic, Gift, ArrowRight, CheckCircle2, Phone, Mail, Send, MessageCircle, Award } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Webinars from "../components/Webinars";
import alinaPhoto from "../assets/team/alina-grigoryan.jpg";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "../hooks/use-toast";
import { useLang } from "../i18n/LangContext";
import { getWebinarT } from "../i18n/webinar_i18n";
import useUTM from "../hooks/useUTM";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BONUS_ICONS = ['📋', '❓', '🎯', '💎'];
const TOPIC_ICONS = ['🇪🇺', '⏳', '🏫', '📄', '💰', '⚠️'];

export const RegisterForm = ({ small }) => {
  const { lang } = useLang();
  const w = getWebinarT(lang);
  const utm = useUTM();
  const [f, setF] = useState({ name: '', phone: '', email: '', country: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.email) { toast({ title: w.required, variant: 'destructive' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { name: f.name, phone: f.phone, email: f.email, country: f.country, program: 'Webinar Europe 2026-2027', message: 'Webinar 19.09.2026 12:00', lang, source: 'webinar-europe-page', ...utm });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'webinar_registration_success', form_source: 'webinar-europe-page' });
      setSent(true);
      toast({ title: w.sent });
    } catch { toast({ title: w.err, description: w.errDesc, variant: 'destructive' }); } finally { setBusy(false); }
  };

  if (sent) {
    const bonus = {
      uk: { kicker: '🎁 Ваучер 100 €', title: 'Підпишись на канал — отримай 100 €', desc: 'Активуй ваучер на 100 € на будь-який пакет послуг STUDYGRI у нашому каналі.', tg: 'Telegram', viber: 'Viber' },
      ru: { kicker: '🎁 Ваучер 100 €', title: 'Подпишись на канал — получи 100 €', desc: 'Активируй ваучер на 100 € на любой пакет услуг STUDYGRI в нашем канале.', tg: 'Telegram', viber: 'Viber' },
      en: { kicker: '🎁 €100 Voucher', title: 'Subscribe & unlock your €100 voucher', desc: 'Claim your €100 voucher towards any STUDYGRI service package in our channel.', tg: 'Telegram', viber: 'Viber' },
      ar: { kicker: '🎁 قسيمة 100 €', title: 'اشترك في القناة واحصل على 100 €', desc: 'فعّل قسيمتك بقيمة 100 € على أي باقة خدمات STUDYGRI في قناتنا.', tg: 'Telegram', viber: 'Viber' },
      it: { kicker: '🎁 Voucher 100 €', title: 'Iscriviti al canale — ricevi 100 €', desc: 'Attiva il voucher da 100 € su qualsiasi pacchetto STUDYGRI nel nostro canale.', tg: 'Telegram', viber: 'Viber' },
      zh: { kicker: '🎁 100 € 礼券', title: '订阅频道，领取 100 € 礼券', desc: '在我们的频道中激活 100 € 礼券，可用于任意 STUDYGRI 服务套餐。', tg: 'Telegram', viber: 'Viber' },
      es: { kicker: '🎁 Bono 100 €', title: 'Suscríbete al canal y obtén 100 €', desc: 'Activa tu bono de 100 € en cualquier paquete STUDYGRI en nuestro canal.', tg: 'Telegram', viber: 'Viber' },
      pl: { kicker: '🎁 Voucher 100 €', title: 'Subskrybuj kanał — odbierz 100 €', desc: 'Aktywuj voucher 100 € na dowolny pakiet STUDYGRI w naszym kanale.', tg: 'Telegram', viber: 'Viber' }
    };
    const b = bonus[lang] || bonus.uk;
    return (
      <div className={`${small ? '' : 'mx-auto'} max-w-xl rounded-2xl p-6 text-left`} style={{ background: 'linear-gradient(135deg, rgba(201,169,110,0.18), rgba(201,169,110,0.05))', border: '1px solid rgba(201,169,110,0.5)' }} data-testid="webinar-page-sent">
        <div className="flex items-center gap-3 mb-4">
          <CheckCircle2 className="w-7 h-7 shrink-0" style={{ color: '#C9A96E' }} />
          <span className="text-base md:text-lg font-semibold" style={{ color: '#1B2A4A' }}>{w.sent}</span>
        </div>
        <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(27,42,74,0.05)' }}>
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase mb-1.5" style={{ color: '#C9A96E' }}>{b.kicker}</div>
          <div className="font-display text-xl md:text-2xl leading-tight mb-1.5" style={{ color: '#1B2A4A', fontFamily: "'Forum','Cormorant Garamond',serif" }}>{b.title}</div>
          <p className="text-sm" style={{ color: '#5a6a6a' }}>{b.desc}</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5">
          <a href="https://t.me/studygriweb" target="_blank" rel="noopener noreferrer" data-testid="wp-tg-btn" className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold uppercase tracking-[0.10em] transition-all hover:scale-[1.02]" style={{ background: '#229ED9', color: '#fff', boxShadow: '0 8px 18px -8px rgba(34,158,217,0.55)' }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"/></svg>
            {b.tg}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3 text-left" data-testid="webinar-page-form">
      <div className="grid md:grid-cols-2 gap-3">
        <div><Label className="text-xs">{w.name} *</Label><Input value={f.name} onChange={e => set('name', e.target.value)} className="mt-1" data-testid="wp-name" /></div>
        <div><Label className="text-xs">{w.phone} *</Label><Input value={f.phone} onChange={e => set('phone', e.target.value)} placeholder="+380..." className="mt-1" data-testid="wp-phone" /></div>
      </div>
      <div><Label className="text-xs">{w.email} *</Label><Input type="email" value={f.email} onChange={e => set('email', e.target.value)} className="mt-1" data-testid="wp-email" /></div>
      <div>
        <Label className="text-xs">{w.countryQ}</Label>
        <Select value={f.country} onValueChange={v => set('country', v)}>
          <SelectTrigger className="mt-1" data-testid="wp-country"><SelectValue placeholder={w.chooseCountry} /></SelectTrigger>
          <SelectContent>{w.countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <button type="submit" disabled={busy} className="w-full h-12 rounded-full font-bold transition hover:opacity-90 inline-flex items-center justify-center gap-2" style={{ background: '#1B2A4A', color: '#C9A96E' }} data-testid="wp-submit">
        {busy ? '...' : w.submit} <ArrowRight className="w-4 h-4" />
      </button>
    </form>
  );
};

const WebinarPage = () => {
  const { lang } = useLang();
  const w = getWebinarT(lang);
  const [, setFormOpen] = useState(false);
  const rtl = lang === 'ar';

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }} dir={rtl ? 'rtl' : 'ltr'}>
      <Header onOpenForm={() => setFormOpen(true)} />

      <main className="pt-28 md:pt-36" data-testid="webinar-page">
        {/* HERO */}
        <section className="py-6 md:py-10" style={{ background: '#1B2A4A', color: '#fff' }}>
          <div className="max-w-4xl mx-auto px-5 md:px-8">
            <div>
              <div className="inline-block text-[10px] md:text-[11px] font-bold tracking-[0.22em] uppercase px-3 py-1 rounded-full mb-3 md:mb-4" style={{ background: '#C9A96E', color: '#1B2A4A' }}>{w.badge}</div>
              <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-balance leading-[1.1]" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{w.h1}</h1>
              <p className="mt-2.5 md:mt-3 text-sm md:text-base lg:text-lg" style={{ color: '#C9A96E' }}>{w.subtitle}</p>

              <div className="mt-4 md:mt-5 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs md:text-sm" style={{ color: '#C9A96E' }}>
                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 shrink-0" /> {w.date}</div>
                <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 shrink-0" /> {w.time}</div>
                <div className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 shrink-0" /> {w.format}</div>
                <div className="flex items-center gap-2"><Mic className="w-3.5 h-3.5 shrink-0" /> {w.speaker}</div>
              </div>

              <a href="#webinar-register" className="mt-5 md:mt-6 inline-flex items-center gap-2 px-6 md:px-7 h-12 md:h-13 rounded-full font-bold text-sm md:text-base transition hover:opacity-90" style={{ background: '#C9A96E', color: '#1B2A4A' }} data-testid="wp-hero-cta">
                {w.heroCta} <ArrowRight className="w-4 h-4" />
              </a>

              <div className="mt-5 md:mt-6 rounded-2xl p-4 md:p-5" style={{ border: '1.5px solid #C9A96E', background: 'rgba(201,169,110,0.08)' }}>
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" style={{ color: '#C9A96E' }} />
                  <div>
                    <div className="font-bold text-sm md:text-base mb-1" style={{ color: '#C9A96E' }}>{w.giftTitle}</div>
                    <div className="text-xs md:text-sm text-white/85 leading-relaxed">{w.giftDesc}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <section id="webinar-register" className="py-16 md:py-20" style={{ background: '#fff' }}>
          <div className="max-w-2xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-3xl md:text-4xl text-center" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.02em' }}>{w.regTitle}</h2>
            <p className="mt-3 text-center text-base" style={{ color: '#666' }}>{w.regDesc}</p>
            <div className="mt-8"><RegisterForm /></div>
          </div>
        </section>

        {/* WEBINARS CAROUSEL */}
        <Webinars />

        {/* TOPICS */}
        <section className="py-16 md:py-20" style={{ background: '#FAF8F5' }}>
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-3xl md:text-4xl text-center mb-12" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.02em' }}>{w.topicsTitle}</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {w.topics.map((tp, i) => (
                <div key={i} className="rounded-2xl p-7 transition hover:-translate-y-1" style={{ background: '#fff', border: '1px solid #E8E0D5' }} data-testid={`wp-topic-${i+1}`}>
                  <div className="text-3xl mb-3">{TOPIC_ICONS[i]}</div>
                  <h3 className="font-display text-xl" style={{ color: '#1B2A4A', fontWeight: 800 }}>{tp.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed" style={{ color: '#666' }}>{tp.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPEAKER */}
        <section className="py-16 md:py-20" style={{ background: '#fff' }}>
          <div className="max-w-5xl mx-auto px-5 md:px-8 grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-2">
              <div className="rounded-3xl overflow-hidden" style={{ border: '2px solid #C9A96E' }}>
                <img src={alinaPhoto} alt="Alina Grigoryan — CEO STUDYGRI" className="w-full h-auto block aspect-square object-cover" data-testid="webinar-speaker-photo" />
              </div>
            </div>
            <div className="md:col-span-3">
              <div className="text-[11px] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: '#C9A96E' }}>{w.speakerKicker}</div>
              <h2 className="font-display text-3xl md:text-4xl" style={{ color: '#1B2A4A', fontWeight: 900, letterSpacing: '-0.02em' }}>{w.speakerName}</h2>
              <p className="mt-2 text-base font-semibold" style={{ color: '#C9A96E' }}>{w.speakerRole}</p>
              <p className="mt-5 text-base leading-relaxed" style={{ color: '#3a4f4f' }}>
                {w.speakerBio}
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {['ICEF', 'BBSN', 'English UK', 'Navitas Champions'].map(b => (
                  <span key={b} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: '#FAF8F5', border: '1px solid #E8E0D5', color: '#1B2A4A' }}>
                    <Award className="w-3 h-3" style={{ color: '#C9A96E' }} /> {b}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* BONUSES */}
        <section className="py-16 md:py-20" style={{ background: '#1B2A4A', color: '#fff' }}>
          <div className="max-w-6xl mx-auto px-5 md:px-8">
            <h2 className="font-display text-3xl md:text-4xl text-center mb-12" style={{ fontWeight: 900, letterSpacing: '-0.02em' }}>{w.bonusesTitle}</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {w.bonuses.map((b, i) => (
                <div key={i} className="rounded-2xl p-7" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid #C9A96E' }} data-testid={`wp-bonus-${i+1}`}>
                  <div className="text-3xl mb-3">{BONUS_ICONS[i]}</div>
                  <h3 className="font-display text-lg" style={{ color: '#C9A96E', fontWeight: 800 }}>{b.t}</h3>
                  {b.d && <p className="mt-2 text-sm text-white/80 leading-relaxed">{b.d}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 md:py-20" style={{ background: 'linear-gradient(135deg, #C9A96E, #E0C58D)', color: '#1B2A4A' }}>
          <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
            <h2 className="font-display text-3xl md:text-5xl text-balance" style={{ fontWeight: 900, letterSpacing: '-0.025em' }}>{w.finalTitle}</h2>
            <p className="mt-4 text-base md:text-lg font-semibold">{w.finalSub}</p>
            <a href="#webinar-register" className="mt-8 inline-flex items-center gap-2 px-8 h-14 rounded-full font-bold text-base text-white transition hover:opacity-90" style={{ background: '#1B2A4A' }} data-testid="wp-final-cta">
              {w.heroCta} <ArrowRight className="w-5 h-5" />
            </a>
            <div className="mt-8 flex flex-wrap justify-center gap-5 text-sm font-semibold">
              <a href="tel:+380504429555" className="inline-flex items-center gap-2 hover:opacity-70"><Phone className="w-4 h-4" /> +38 050 44 29 555</a>
              <a href="mailto:contact@studygri.com" className="inline-flex items-center gap-2 hover:opacity-70"><Mail className="w-4 h-4" /> contact@studygri.com</a>
              <a href="https://t.me/manager_studygri" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-70"><Send className="w-4 h-4" /> Telegram</a>
              <a href="https://api.whatsapp.com/send/?phone=%2B31638633238" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:opacity-70"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WebinarPage;
